import globalNotification from '../../common/hooks/globalNotification';
import { notificationType } from '../../common/constants/notificationType';
import { Upload } from 'antd';
import i18next from 'i18next';

export const beforeUpload = (file, acceptFileTypes = [], maxFileSizeMb = null, t) => {
    const isValidType = acceptFileTypes.length === 0 || acceptFileTypes.some(type => file.type === type);

    if (!isValidType) {
        globalNotification.open({
            type: notificationType.WARNING,
            message: 'Warning',
            description: (
                <p>
                    {t('form:notifications.wrong-extension')}
                    <strong> {acceptFileTypes.join(', ')}</strong>
                </p>
            ),
        });
    }

    const fileSizeInMB = file.size / (1024 * 1024);
    const isValidSize = maxFileSizeMb ? fileSizeInMB < maxFileSizeMb : true;
    if (!isValidSize) {
        globalNotification.open({
            type: notificationType.WARNING,
            message: 'Warning',
            // description: `File size is too large! Please upload file below ${maxFileSizeMb}MB`,
            description: (
                <p>
                    {t('form:notifications.fize-size')} <strong>{maxFileSizeMb}MB</strong>.{' '}
                    {t('form:notifications.select-another-file')}.
                </p>
            ),
        });
    }

    // You can reuse this method in different components by adding validation rules in parameters, like was added type and size.

    return (isValidType && isValidSize) || Upload.LIST_IGNORE;
};

// PDF_PASSWORD_PROTECTED: the PDF asks for a password to open; a PDF with restrictions only is unlocked by the API.
// Other refusals get the translated generic line (the API's own messages are English only).
export const uploadErrorText = error =>
    error?.response?.data?.code === 'PDF_PASSWORD_PROTECTED'
        ? i18next.t('form:notifications.pdf-password')
        : i18next.t('form:notifications.upload-failed');

// axiosService already shows every error but a 400 / 423 (it leaves those to the caller), so only those, and errors
// that never went through axios, get a toast here: the user never sees two for one failure
const callerShowsError = error => !error?.isAxiosError || [400, 423].includes(error?.response?.status);

const notifyUploadFailed = async (file, error) => {
    try {
        // the page that uploads may not have loaded the 'form' strings yet
        await i18next.loadNamespaces('form');
    } catch (e) {
        // the keys still show; nothing else to do
    }
    globalNotification.open({
        type: notificationType.ERROR,
        message: i18next.t('form:notifications.upload-failed-title'),
        description: (
            <p>
                <strong>{file?.name}</strong> - {uploadErrorText(error)}
            </p>
        ),
    });
};

/** Uploads one file; resolves true when it was saved, false (after telling the user why) when it wasn't. */
export const handleFileUpload = async (
    path,
    { file, onSuccess, onProgress, onError },
    uploadFile,
    fieldName,
    id,
    callback = () => {},
    code,
    flag
) => {
    // We are using different field names from property 'fieldName' to separate access and specify required options on the back-end endpoint.
    try {
        const data = {
            [fieldName]: file,
        };
        if (id) {
            data.id = id;
            data.code = code;
            data.flag = flag;
        }

        const response = await uploadFile(path, data, {
            onUploadProgress: progressEvent => {
                const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                // Update progress using onProgress callback
                onProgress({ percent });
            },
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        // Call onSuccess callback when upload is successful
        onSuccess(response.data, { ...file, name: response.data });
        callback();
        return true;
    } catch (error) {
        console.log(error);
        if (callerShowsError(error)) await notifyUploadFailed(file, error);
        if (onError) onError(error);
        return false;
    }
};

export const handleChange = (info, t) => {
    const { status, name, response } = info.file;
    if (status === 'done' && response) {
        globalNotification.open({
            type: notificationType.SUCCESS,
            message: 'Success',
            description: (
                <p>
                    <strong>{name}</strong> - {t('form:notifications.upload-succesfully')}.
                </p>
            ),
        });
    } // we don't handle 'error' status here because there is interceptor that does this and calls error notification
};

export const handleRemove = async (file, path, deleteFile, t, callback = () => {}) => {
    if (file) {
        await deleteFile(path, file.response || file.name);

        if (file.status === 'done') {
            globalNotification.open({
                type: notificationType.SUCCESS,
                message: 'Success',
                description: (
                    <p>
                        <strong>{file.name}</strong> - {t('form:notifications.removed-succesfully')}.
                    </p>
                ),
            });
            callback();
        }
    }
};
